import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './tooltip.scss';
import TooltipBody from './tooltip-body';

function DynamicTooltip({
  className,
  delay = 100,
  zIndex,
  wrapperClass,
  direction: propDirection,
  content,
  children,
}) {
  const timeoutRef = useRef(null);

  const [active, setActive] = useState(false);
  const [leftPosition, setLeftPosition] = useState(0);
  const [topPosition, setTopPosition] = useState(0);
  const [direction, setDirection] = useState('bottom');
  const [dynamicTooltip, setDynamicTooltip] = useState(false);

  const tooltipContainer = useRef(null);
  const tooltipHandler = useRef(null);
  const tooltipLimit = useRef(null);
  const actualContent = useRef(null);

  const showTip = () => {
    timeoutRef.current = setTimeout(() => {
      if (!tooltipHandler.current) return;

      const rect = tooltipHandler.current.getBoundingClientRect();

      const tooltipRect = tooltipContainer.current?.getBoundingClientRect() || {
        width: 0,
        height: 0,
      };

      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      const dir = propDirection || 'bottom';

      switch (dir) {
        case 'right':
          setLeftPosition(rect.x + rect.width);
          setTopPosition(rect.y + rect.height / 2 - tooltipHeight / 2);
          break;

        case 'top':
          setLeftPosition(rect.x + rect.width / 2 - tooltipWidth / 2);
          setTopPosition(rect.y - tooltipHeight - 10);
          break;

        case 'left':
          setLeftPosition(rect.x - tooltipWidth);
          setTopPosition(rect.y + rect.height / 2 - tooltipHeight / 2);
          break;

        case 'bottom-left':
          setLeftPosition(rect.x);
          setTopPosition(rect.y + rect.height);
          break;

        case 'center':
          setLeftPosition(rect.x + rect.width / 2 - tooltipWidth / 2);
          setTopPosition(rect.y + rect.height);
          break;

        default:
          setLeftPosition(rect.x + rect.width / 2 - tooltipWidth / 2);

          if (rect.y + rect.height + tooltipHeight + 20 > window.innerHeight) {
            setTopPosition(rect.y - tooltipHeight - 10);
            setDirection('top');
          } else {
            setTopPosition(rect.y + rect.height + 8);
            setDirection('bottom');
          }
      }

      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    clearTimeout(timeoutRef.current);
    setActive(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const limitWidth =
        tooltipLimit.current?.getBoundingClientRect().width || 0;
      const contentWidth =
        actualContent.current?.getBoundingClientRect().width || 0;

      setDynamicTooltip(contentWidth > limitWidth);
    }, 100);

    return () => clearTimeout(timer);
  }, [content]);

  return (
    <span
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onMouseDown={hideTip}
      role="button"
      tabIndex={0}
      ref={tooltipHandler}
      className={`tooltip-wrapper w-100 ${wrapperClass}`}
    >
      <span className="tooltip-limit" ref={tooltipLimit}>
        {children}
      </span>

      <span className="actual-content" ref={actualContent}>
        {content}
      </span>

      {dynamicTooltip && (
        <TooltipBody
          ref={tooltipContainer}
          content={content}
          topPosition={topPosition}
          leftPosition={leftPosition}
          direction={propDirection || direction}
          className={className}
          active={active}
          zIndex={zIndex}
        />
      )}
    </span>
  );
}

export default DynamicTooltip;

DynamicTooltip.propTypes = {
  delay: PropTypes.number,
  direction: PropTypes.string,
  content: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  zIndex: PropTypes.number,
  wrapperClass: PropTypes.string,
};

DynamicTooltip.defaultProps = {
  delay: 100,
  direction: '',
  content: '',
  className: '',
  zIndex: 1000000,
  wrapperClass: '',
};
